// client/src/components/influencer/InfluencerMyInfoChange.js
// 내 정보 변경 — A안 리디자인: 기본 정보 라벨 카드 + 배송지 주소 카드 (조회·캐시·주소 변경 로직은 기존 그대로)
// 계약 상태는 '프리랜서 계약' 화면이 전담 (v4)
import React, { useEffect, useState } from 'react';
import AddressSearch from '../AddressSearch';

const API_URL = process.env.REACT_APP_API_URL;

// 캐시 키
const keysOf = (uk) => ({
  name:   `influencer_profile_name:${uk}`,
  phone:  `influencer_profile_phone:${uk}`,
  email:  `influencer_profile_email:${uk}`,
  addr:   `influencer_profile_addr:${uk}`,
  bHold:  `influencer_bank_holder:${uk}`,
  bName:  `influencer_bank_name:${uk}`,
  bMask:  `influencer_bank_masked:${uk}`,
  cStat:  `influencer_contract_status:${uk}`,
});

// 동기 캐시 읽기 → 첫 렌더 즉시 반영
function readCached(userKey) {
  if (!userKey) return { profile: null, contractStatus: '' };
  const k = keysOf(userKey);
  const cached = {
    userName:      localStorage.getItem(k.name)  || '',
    phoneNumber:   localStorage.getItem(k.phone) || '',
    email:         localStorage.getItem(k.email) || '',
    userAddress:   localStorage.getItem(k.addr)  || '',
    bank: {
      holderName:  localStorage.getItem(k.bHold) || '',
      bankName:    localStorage.getItem(k.bName) || '',
      maskedAccount: localStorage.getItem(k.bMask) || ''
    }
  };
  const hasAny = cached.userName || cached.phoneNumber || cached.email || cached.userAddress || cached.bank.maskedAccount;
  return {
    profile: hasAny ? cached : null,
    contractStatus: localStorage.getItem(k.cStat) || ''
  };
}

function InfluencerMyInfoChange() {
  // 유저키 선캐시 로드
  const initialUserKey = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
  const { profile: cachedProfile, contractStatus: cachedContract } = readCached(initialUserKey);

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(cachedProfile);
  const [contractStatus, setContractStatus] = useState(cachedContract);

  // 주소 변경 모달
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addr, setAddr] = useState({ postcode: '', address: '', detailAddress: '' });

  // 알림 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const openModal = (msg) => { setModalMsg(msg); setModalOpen(true); };

  // 사용자 확정
  const [userKey, setUserKey] = useState(initialUserKey);
  const [userReady, setUserReady] = useState(!!initialUserKey);

  // 서버 확정 + 상태 동기화
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const me = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
        const mj = await me.json().catch(() => ({}));
        if (!ignore && me.ok && mj?.ok) {
          const uk = (mj?.user?.userId || '').toLowerCase();
          if (uk) {
            setUserKey(uk);
            sessionStorage.setItem('current_user_key', uk);
            const k = keysOf(uk);

            // 1) 프로필 조회
            try {
              const pr = await fetch(`${API_URL}/api/influencer/mybiz/profile`, { credentials: 'include' });
              const pj = await pr.json().catch(() => ({}));
              if (pr.ok && pj?.ok) {
                const next = {
                  userName: pj.userName || '',
                  phoneNumber: pj.phoneNumber || '',
                  email: pj.email || '',
                  userAddress: pj.userAddress || '',
                  bank: {
                    holderName: pj.bank?.holderName || '',
                    bankName: pj.bank?.bankName || '',
                    maskedAccount: pj.bank?.maskedAccount || ''
                  }
                };
                setProfile(next);
                localStorage.setItem(k.name,  next.userName);
                localStorage.setItem(k.phone, next.phoneNumber);
                localStorage.setItem(k.email, next.email);
                localStorage.setItem(k.addr,  next.userAddress);
                if (next.bank?.holderName) localStorage.setItem(k.bHold, next.bank.holderName);
                if (next.bank?.bankName)   localStorage.setItem(k.bName, next.bank.bankName);
                if (next.bank?.maskedAccount) localStorage.setItem(k.bMask, next.bank.maskedAccount);
              }
            } catch {}

            // 2) 계약 상태 조회
            try {
              const sr = await fetch(`${API_URL}/api/influencer/mybiz/working-contract/status`, { credentials: 'include' });
              const sj = await sr.json().catch(() => ({}));
              if (sr.ok && sj?.ok) {
                setContractStatus(sj.status || 'none');
                localStorage.setItem(k.cStat, sj.status || 'none');
              }
            } catch {}
          }
        }
      } finally {
        if (!ignore) setUserReady(true);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const openAddrModal = () => {
    setAddr({ postcode: '', address: '', detailAddress: '' });
    setAddrModalOpen(true);
  };

  const handleSubmitAddress = async () => {
    if (loading) return;
    if (!userReady || !userKey) { openModal('세션 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.'); return; }

    const { postcode, address, detailAddress } = addr;
    if (!postcode.trim() || !address.trim() || !detailAddress.trim()) {
      openModal('우편번호, 주소, 상세주소를 모두 입력해주세요.');
      return;
    }
    const userAddress = `${postcode} ${address} ${detailAddress}`.trim();

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/profile/address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userAddress })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setProfile((p) => ({ ...(p || {}), userAddress }));
        setAddrModalOpen(false);
        const k = keysOf(userKey);
        localStorage.setItem(k.addr, userAddress);
        openModal('주소가 변경되었습니다.');
      } else {
        openModal(data?.message || '주소 변경에 실패했습니다.');
      }
    } catch {
      openModal('서버 요청 실패');
    }
    setLoading(false);
  };

  if (!userReady && !initialUserKey) return null;

  const bankLabel = profile?.bank?.maskedAccount
    ? `${profile.bank.bankName || ''} ${profile.bank.maskedAccount}`.trim()
    : '';

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">내 정보 변경</h2>
        <p className="mb-page-description">기본 정보를 확인하고 주소를 변경할 수 있어요.</p>
      </div>

      {!profile && <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>불러오는 중...</div>}

      {profile && (
        <>
          {/* 기본 정보 */}
          <div className="mb-card">
            <h3 className="mb-section-title">기본 정보</h3>
            <div className="mb-grid-2">
              <div className="mb-info-field" style={{ marginBottom: 0 }}>
                <span className="mb-info-label">이름</span>
                <div className="mb-info-value">{profile.userName || '-'}</div>
              </div>
              <div className="mb-info-field" style={{ marginBottom: 0 }}>
                <span className="mb-info-label">휴대폰 번호</span>
                <div className="mb-info-value">{profile.phoneNumber || '-'}</div>
              </div>
              <div className="mb-info-field" style={{ marginBottom: 0 }}>
                <span className="mb-info-label">이메일</span>
                <div className="mb-info-value">{profile.email || '-'}</div>
              </div>
              <div className="mb-info-field" style={{ marginBottom: 0 }}>
                <span className="mb-info-label">환전 통장</span>
                <div className="mb-info-value">
                  {bankLabel || <span style={{ color: '#6b7686', fontSize: 14 }}>등록된 통장이 없어요</span>}
                </div>
              </div>
            </div>
            <div className="mb-info-hint" style={{ marginTop: 12 }}>
              이름·연락처·이메일 변경은 문의하기를 통해 진행해주세요.
              {contractStatus !== 'completed' && ' 포인트 환전에는 프리랜서 계약이 필요해요.'}
            </div>
          </div>

          {/* 배송지 주소 */}
          <div className="mb-card">
            <h3 className="mb-section-title">배송지 주소</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {profile.userAddress || '등록된 주소가 없어요'}
                </div>
                <div className="mb-info-hint" style={{ marginTop: 2 }}>
                  배송형 프로젝트의 제품이 이 주소로 배송돼요
                </div>
              </div>
              <button
                type="button"
                className="mb-btn mb-btn-secondary"
                onClick={openAddrModal}
                disabled={loading}
              >
                주소 변경
              </button>
            </div>
          </div>
        </>
      )}

      {/* 주소 변경 모달 */}
      {addrModalOpen && (
        <div className="mbm-overlay">
          <div className="mb-card mbm" style={{ width: 520, maxWidth: '92vw', margin: 0, padding: 28 }}>
            <h3 className="mb-section-title" style={{ marginBottom: 20 }}>주소 변경</h3>
            <AddressSearch formData={addr} setFormData={setAddr} loading={loading} />
            <div className="mbm-foot" style={{ marginTop: 20 }}>
              <button
                type="button"
                className="mb-btn mb-btn-secondary"
                onClick={() => setAddrModalOpen(false)}
                disabled={loading}
              >
                취소
              </button>
              <button
                type="button"
                className="mb-btn mb-btn-primary"
                onClick={handleSubmitAddress}
                disabled={loading}
              >
                {loading ? '변경 중...' : '변경하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 모달 */}
      {modalOpen && (
        <div className="mbm-overlay" onClick={() => setModalOpen(false)}>
          <div className="mb-card mbm" style={{ width: 360, margin: 0, textAlign: 'center', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>{modalMsg}</p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setModalOpen(false)}>확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluencerMyInfoChange;
